import { useState, useEffect, useCallback } from 'react';
import { Shield, FileText, Save, ExternalLink, Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Heading2, Heading3, Link2, Quote, Undo2, Redo2 } from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TipTapLink from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';

const API = process.env.REACT_APP_BACKEND_URL;

function ToolbarBtn({ onClick, active, children, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
        active ? 'bg-[#00E5FF]/20 text-[#00E5FF]' : 'text-slate-400 hover:text-white hover:bg-white/10'
      }`}
    >
      {children}
    </button>
  );
}

function EditorToolbar({ editor }) {
  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  };

  return (
    <div className="flex items-center flex-wrap gap-0.5 px-2 py-1.5 border-b border-white/10 bg-white/[0.02]">
      <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold">
        <Bold className="w-3.5 h-3.5" />
      </ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic">
        <Italic className="w-3.5 h-3.5" />
      </ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline">
        <UnderlineIcon className="w-3.5 h-3.5" />
      </ToolbarBtn>
      <div className="w-px h-5 bg-white/10 mx-1" />
      <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2">
        <Heading2 className="w-3.5 h-3.5" />
      </ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3">
        <Heading3 className="w-3.5 h-3.5" />
      </ToolbarBtn>
      <div className="w-px h-5 bg-white/10 mx-1" />
      <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet List">
        <List className="w-3.5 h-3.5" />
      </ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered List">
        <ListOrdered className="w-3.5 h-3.5" />
      </ToolbarBtn>
      <div className="w-px h-5 bg-white/10 mx-1" />
      <ToolbarBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Quote">
        <Quote className="w-3.5 h-3.5" />
      </ToolbarBtn>
      <ToolbarBtn onClick={addLink} active={editor.isActive('link')} title="Link">
        <Link2 className="w-3.5 h-3.5" />
      </ToolbarBtn>
      <div className="w-px h-5 bg-white/10 mx-1" />
      <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} title="Undo">
        <Undo2 className="w-3.5 h-3.5" />
      </ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} title="Redo">
        <Redo2 className="w-3.5 h-3.5" />
      </ToolbarBtn>
    </div>
  );
}

function LegalDocEditor({ icon: Icon, docKey, apiPath, previewPath }) {
  const [title, setTitle] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [initialContent, setInitialContent] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      TipTapLink.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'text-[#00B4D8] underline' },
      }),
      Underline,
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose prose-invert prose-sm max-w-none min-h-[300px] focus:outline-none px-4 py-3 prose-headings:font-["Outfit"] prose-p:leading-relaxed prose-a:text-[#00B4D8]',
      },
    },
  });

  const fetchDoc = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/legal/${apiPath}`);
      const data = await res.json();
      setTitle(data.title || '');
      setLastUpdated(data.last_updated || '');
      setInitialContent(data.content || '');
    } catch (err) {
      console.error(`Failed to fetch ${docKey}:`, err);
    } finally {
      setLoading(false);
    }
  }, [apiPath, docKey]);

  useEffect(() => { fetchDoc(); }, [fetchDoc]);

  useEffect(() => {
    if (editor && initialContent && !editor.isDestroyed) {
      editor.commands.setContent(initialContent);
    }
  }, [editor, initialContent]);

  const handleSave = async () => {
    if (!editor) return;
    setSaving(true);
    setSaved(false);
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${API}/api/admin/legal/${apiPath}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title, last_updated: lastUpdated, content: editor.getHTML() }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#111111] border border-white/5 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center"><Icon className="w-4 h-4 text-slate-500" /></div>
          <div className="h-5 w-32 bg-white/5 rounded animate-pulse" />
        </div>
        <div className="space-y-3">
          <div className="h-10 bg-white/5 rounded-xl animate-pulse" />
          <div className="h-10 bg-white/5 rounded-xl animate-pulse" />
          <div className="h-48 bg-white/5 rounded-xl animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`legal-editor-${docKey}`} className="bg-[#111111] border border-white/5 rounded-2xl p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#00E5FF]/10 flex items-center justify-center">
            <Icon className="w-4 h-4 text-[#00E5FF]" />
          </div>
          <h3 className="font-['Outfit'] text-lg font-bold text-white">{title || docKey}</h3>
        </div>
        <a
          href={previewPath}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`legal-preview-link-${docKey}`}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#00E5FF] transition-colors"
        >
          <ExternalLink className="w-3 h-3" />
          View Page
        </a>
      </div>

      {/* Fields */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-slate-400 block mb-1.5">Page Title</label>
            <input
              data-testid={`legal-title-${docKey}`}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50"
              placeholder="Privacy Policy"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-400 block mb-1.5">Last Updated</label>
            <input
              data-testid={`legal-updated-${docKey}`}
              value={lastUpdated}
              onChange={(e) => setLastUpdated(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50"
              placeholder="April 2026"
            />
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-slate-400 block mb-1.5">Content</label>
          {editor && (
            <div className="border border-white/10 rounded-xl overflow-hidden bg-white/5">
              <EditorToolbar editor={editor} />
              <EditorContent data-testid={`legal-content-${docKey}`} editor={editor} />
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            data-testid={`legal-save-${docKey}`}
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#00E5FF] text-[#0A0A0A] px-6 py-3 rounded-xl text-sm font-semibold hover:bg-white transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          {saved && <span className="text-xs text-emerald-400">Saved successfully</span>}
        </div>
      </div>
    </div>
  );
}

export default function LegalManager() {
  return (
    <div data-testid="legal-manager">
      <div className="mb-8">
        <h2 className="font-['Outfit'] text-xl font-bold text-white">Legal Pages</h2>
        <p className="text-sm text-slate-500 mt-0.5">Edit the content for your Privacy Policy and Terms of Service pages.</p>
      </div>

      <div className="space-y-6">
        <LegalDocEditor
          icon={Shield}
          docKey="privacy"
          apiPath="privacy-policy"
          previewPath="/privacy-policy"
        />
        <LegalDocEditor
          icon={FileText}
          docKey="terms"
          apiPath="terms-of-service"
          previewPath="/terms-of-service"
        />
      </div>
    </div>
  );
}
